import { InformationCircleIcon } from '@heroicons/react/24/outline'
import { useRef, useState } from 'react'
import { createPortal } from 'react-dom'

// Small "i" icon that reveals an explanatory tooltip on hover or focus. Used to
// document how each metric is calculated so the answer to "where does this
// number come from?" lives next to the number.
//
// The tooltip renders into a portal with fixed positioning, measured from the
// icon on open, so it never gets clipped by a card edge or an overflow-auto
// table wrapper. It clamps to the viewport horizontally and flips above the icon
// when there isn't room below.
const WIDTH = 256
const EST_HEIGHT = 96

export default function InfoTip({ text }: { text: string }) {
    const ref = useRef<HTMLSpanElement>(null)
    const [pos, setPos] = useState<{ top: number; left: number } | null>(null)

    function show() {
        const el = ref.current
        if (!el) return
        const r = el.getBoundingClientRect()
        const left = Math.min(Math.max(8, r.left), window.innerWidth - WIDTH - 8)
        const below = r.bottom + 6
        const top = below + EST_HEIGHT > window.innerHeight ? Math.max(8, r.top - EST_HEIGHT) : below
        setPos({ top, left })
    }

    function hide() {
        setPos(null)
    }

    return (
        <span
            ref={ref}
            role='button'
            tabIndex={0}
            aria-label='How this is calculated'
            onMouseEnter={show}
            onMouseLeave={hide}
            onFocus={show}
            onBlur={hide}
            onClick={(e) => {
                e.preventDefault()
                e.stopPropagation()
            }}
            className='ml-1 inline-flex cursor-help align-middle text-neutral-400 hover:text-neutral-600 focus:text-neutral-600 focus:outline-none'
        >
            <InformationCircleIcon className='w-3.5 h-3.5' />
            {pos &&
                createPortal(
                    <span
                        role='tooltip'
                        style={{ position: 'fixed', top: pos.top, left: pos.left, width: WIDTH }}
                        className='z-[100] rounded-lg bg-neutral-900 px-3 py-2 text-xs font-normal normal-case leading-snug tracking-normal text-white shadow-lg'
                    >
                        {text}
                    </span>,
                    document.body
                )}
        </span>
    )
}
